import {
	skipToken,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import type {
	AdminCreateVenue,
	AdminUpdateVenue,
	BulkCancellation,
	CancelReason,
	ClosureId,
	CourtId,
	CreateClosure,
	CreateCourt,
	HourEntry,
	ManualSettlement,
	Policy,
	PriceRuleBody,
	PriceRuleId,
	ReservationId,
	SignUpVenue,
	StaffRecurrenceRequest,
	StaffReservationRequest,
	StaffRole,
	UpdateCourt,
	UpdateVenue,
	UpdateVenueProfile,
	UserId,
	VenueId,
} from "../../schemas/venue";
import { type CalendarQuery, venueApi } from "../../venue-api";
import { queryKeys } from "../query-keys";
import {
	ADMIN_VENUES_PAGE_SIZE,
	flattenPages,
	nextPageParam,
	VENUE_CLOSURES_PAGE_SIZE,
} from "./pagination";

export * from "./court-batch";
export * from "./domains";
export * from "./location-requests";
export * from "./pagination";
export * from "./photos";

const keys = queryKeys.venuePortal;

// Reads take `VenueId | null` (no venue chosen yet) and use `skipToken`, so the
// fetch can never run without an id and no `!` assertion is needed.

export function useMyVenues() {
	return useQuery({ queryFn: () => venueApi.venues.list(), queryKey: keys.venues() });
}

export function useVenue(venueId: VenueId | null) {
	return useQuery({
		queryFn: venueId ? () => venueApi.venues.get(venueId) : skipToken,
		queryKey: keys.venue(venueId ?? ""),
	});
}

export function useVenueCourts(venueId: VenueId | null) {
	return useQuery({
		queryFn: venueId ? () => venueApi.courts.list(venueId) : skipToken,
		queryKey: keys.courts(venueId ?? ""),
	});
}

export function useVenueOpeningHours(venueId: VenueId | null) {
	return useQuery({
		queryFn: venueId ? () => venueApi.hours.get(venueId) : skipToken,
		queryKey: keys.hours(venueId ?? ""),
	});
}

export function useVenueClosures(venueId: VenueId | null, from: string, to: string) {
	const query = useInfiniteQuery({
		getNextPageParam: nextPageParam,
		initialPageParam: undefined as string | undefined,
		queryFn: venueId
			? ({ pageParam }: { pageParam: string | undefined }) =>
					venueApi.closures.list(venueId, from, to, {
						cursor: pageParam,
						limit: VENUE_CLOSURES_PAGE_SIZE,
					})
			: skipToken,
		queryKey: keys.closures(venueId ?? "", from, to),
	});
	return { ...query, closures: flattenPages(query.data?.pages) };
}

export function useVenuePriceRules(venueId: VenueId | null) {
	return useQuery({
		queryFn: venueId ? () => venueApi.prices.list(venueId) : skipToken,
		queryKey: keys.prices(venueId ?? ""),
	});
}

export function useVenueProfile(venueId: VenueId | null) {
	return useQuery({
		queryFn: venueId ? () => venueApi.venues.getProfile(venueId) : skipToken,
		queryKey: keys.profile(venueId ?? ""),
	});
}

export function useVenueMembers(venueId: VenueId | null) {
	return useQuery({
		queryFn: venueId ? () => venueApi.members.list(venueId) : skipToken,
		queryKey: keys.members(venueId ?? ""),
	});
}

/** A desk tablet stays open all day: online bookings must appear without a reload. */
const CALENDAR_REFRESH_MS = 60_000;

export function useVenueCalendar(venueId: VenueId | null, query: CalendarQuery) {
	return useQuery({
		queryFn: venueId ? () => venueApi.reservations.calendar(venueId, query) : skipToken,
		queryKey: keys.calendar(venueId ?? "", query),
		refetchInterval: CALENDAR_REFRESH_MS,
		refetchOnWindowFocus: true,
	});
}

/** One-off read of a calendar range for a download; nothing is cached or kept. */
export const useExportVenueBookings = (venueId: VenueId) =>
	useMutation({
		mutationFn: (query: CalendarQuery) => venueApi.reservations.calendar(venueId, query),
	});

/** Owners and managers: the API answers 403 to the desk. */
export function useVenueReport(venueId: VenueId | null, from: string, to: string) {
	return useQuery({
		queryFn: venueId ? () => venueApi.reports.get(venueId, from, to) : skipToken,
		queryKey: keys.report(venueId ?? "", from, to),
		retry: false,
	});
}

// Mutations take a definite VenueId: a screen only offers an action once a venue
// is chosen. Each invalidates exactly the slices it changes.

export function useSignUpVenue() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: SignUpVenue) => venueApi.venues.signUp(body),
		onSuccess: () => qc.invalidateQueries({ queryKey: keys.venues() }),
	});
}

function useVenueMutation<TVariables, TResult>(
	venueId: VenueId,
	mutationFn: (variables: TVariables) => Promise<TResult>,
	invalidates: ReadonlyArray<(venueId: string) => readonly unknown[]>,
) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn,
		onSuccess: () =>
			Promise.all([
				...invalidates.map((key) => qc.invalidateQueries({ queryKey: key(venueId) })),
				qc.invalidateQueries({ queryKey: keys.venues() }),
			]),
	});
}

export const useUpdateVenue = (venueId: VenueId) =>
	useVenueMutation(venueId, (body: UpdateVenue) => venueApi.venues.update(venueId, body), [
		keys.venue,
	]);

export const useUpdateVenuePolicy = (venueId: VenueId) =>
	useVenueMutation(venueId, (policy: Policy) => venueApi.venues.updatePolicy(venueId, policy), [
		keys.venue,
	]);

export const useUpdateVenueProfile = (venueId: VenueId) =>
	useVenueMutation(
		venueId,
		(body: UpdateVenueProfile) => venueApi.venues.updateProfile(venueId, body),
		[keys.profile],
	);

export const usePublishVenue = (venueId: VenueId) =>
	useVenueMutation(venueId, () => venueApi.venues.publish(venueId), [keys.venue]);

export const useCreateVenueCourt = (venueId: VenueId) =>
	useVenueMutation(venueId, (body: CreateCourt) => venueApi.courts.create(venueId, body), [
		keys.courts,
	]);

export const useUpdateVenueCourt = (venueId: VenueId) =>
	useVenueMutation(
		venueId,
		({ courtId, body }: { courtId: CourtId; body: UpdateCourt }) =>
			venueApi.courts.update(venueId, courtId, body),
		[keys.courts],
	);

export const useDeleteVenueCourt = (venueId: VenueId) =>
	useVenueMutation(venueId, (courtId: CourtId) => venueApi.courts.remove(venueId, courtId), [
		keys.courts,
	]);

export const useReplaceVenueHours = (venueId: VenueId) =>
	useVenueMutation(venueId, (entries: HourEntry[]) => venueApi.hours.replace(venueId, entries), [
		keys.hours,
	]);

export const useCreateVenueClosure = (venueId: VenueId) =>
	useVenueMutation(venueId, (body: CreateClosure) => venueApi.closures.create(venueId, body), [
		(id) => ["venue-portal", id, "closures"],
	]);

export const useDeleteVenueClosure = (venueId: VenueId) =>
	useVenueMutation(
		venueId,
		(closureId: ClosureId) => venueApi.closures.remove(venueId, closureId),
		[(id) => ["venue-portal", id, "closures"]],
	);

export const useCreateVenuePriceRule = (venueId: VenueId) =>
	useVenueMutation(venueId, (body: PriceRuleBody) => venueApi.prices.create(venueId, body), [
		keys.prices,
	]);

export const useUpdateVenuePriceRule = (venueId: VenueId) =>
	useVenueMutation(
		venueId,
		({ ruleId, body }: { ruleId: PriceRuleId; body: PriceRuleBody }) =>
			venueApi.prices.update(venueId, ruleId, body),
		[keys.prices],
	);

export const useDeleteVenuePriceRule = (venueId: VenueId) =>
	useVenueMutation(venueId, (ruleId: PriceRuleId) => venueApi.prices.remove(venueId, ruleId), [
		keys.prices,
	]);

export const useAddVenueMember = (venueId: VenueId) =>
	useVenueMutation(
		venueId,
		({ email, role }: { email: string; role: StaffRole }) =>
			venueApi.members.add(venueId, email, role),
		[keys.members],
	);

export const useChangeVenueMemberRole = (venueId: VenueId) =>
	useVenueMutation(
		venueId,
		({ userId, role }: { userId: UserId; role: StaffRole }) =>
			venueApi.members.changeRole(venueId, userId, role),
		[keys.members],
	);

export const useRemoveVenueMember = (venueId: VenueId) =>
	useVenueMutation(venueId, (userId: UserId) => venueApi.members.remove(venueId, userId), [
		keys.members,
	]);

/** A booking change moves the calendar and the overview's numbers. */
const BOOKINGS_CHANGED = [keys.reservations, keys.reports];

export const useCreateStaffReservation = (venueId: VenueId) =>
	useVenueMutation(
		venueId,
		(body: StaffReservationRequest) => venueApi.reservations.create(venueId, body),
		BOOKINGS_CHANGED,
	);

export const useCreateVenueRecurrence = (venueId: VenueId) =>
	useVenueMutation(
		venueId,
		(body: StaffRecurrenceRequest) => venueApi.recurrences.create(venueId, body),
		BOOKINGS_CHANGED,
	);

export const useCancelVenueRecurrence = (venueId: VenueId) =>
	useVenueMutation(
		venueId,
		({ seriesId, reason }: { seriesId: string; reason?: CancelReason }) =>
			venueApi.recurrences.cancel(venueId, seriesId, reason),
		BOOKINGS_CHANGED,
	);

export const useCancelVenueReservation = (venueId: VenueId) =>
	useVenueMutation(
		venueId,
		({ reservationId, reason }: { reservationId: ReservationId; reason?: CancelReason }) =>
			venueApi.reservations.cancel(venueId, reservationId, reason),
		BOOKINGS_CHANGED,
	);

export const useMarkNoShow = (venueId: VenueId) =>
	useVenueMutation(
		venueId,
		(reservationId: ReservationId) => venueApi.reservations.markNoShow(venueId, reservationId),
		BOOKINGS_CHANGED,
	);

export const useBulkCancelVenue = (venueId: VenueId) =>
	useVenueMutation(
		venueId,
		(body: BulkCancellation) => venueApi.reservations.bulkCancel(venueId, body),
		BOOKINGS_CHANGED,
	);

export const useMoveVenueReservation = (venueId: VenueId) =>
	useVenueMutation(
		venueId,
		({
			reservationId,
			courtId,
			startsAt,
		}: {
			reservationId: ReservationId;
			courtId: CourtId;
			startsAt: string;
		}) => venueApi.reservations.move(venueId, reservationId, { courtId, startsAt }),
		BOOKINGS_CHANGED,
	);

export const useSetVenueSettlement = (venueId: VenueId) =>
	useVenueMutation(
		venueId,
		({
			reservationId,
			settlement,
		}: {
			reservationId: ReservationId;
			settlement: ManualSettlement;
		}) => venueApi.reservations.setSettlement(venueId, reservationId, settlement),
		BOOKINGS_CHANGED,
	);

// Platform-admin: every venue, and the location link that puts a self-served
// venue on its location's courts page.

export function useAdminVenues() {
	const query = useInfiniteQuery({
		getNextPageParam: nextPageParam,
		initialPageParam: undefined as string | undefined,
		queryFn: ({ pageParam }) =>
			venueApi.admin.list({ cursor: pageParam, limit: ADMIN_VENUES_PAGE_SIZE }),
		queryKey: [...keys.all(), "admin", "venues"],
	});
	return { ...query, venues: flattenPages(query.data?.pages) };
}

export function useAdminCreateVenue() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: AdminCreateVenue) => venueApi.admin.create(body),
		onSuccess: () => qc.invalidateQueries({ queryKey: keys.all() }),
	});
}

export function useAdminUpdateVenue(venueId: VenueId) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: AdminUpdateVenue) => venueApi.admin.update(venueId, body),
		onSuccess: () => qc.invalidateQueries({ queryKey: keys.all() }),
	});
}

// One venue as the platform sees it. Every read hangs under one prefix so a
// support action refreshes the page's whole picture with one invalidation.
const adminVenueKey = (venueId: string) => [...keys.all(), "admin", "venue", venueId] as const;

export function useAdminVenue(venueId: VenueId | null) {
	return useQuery({
		queryFn: venueId ? () => venueApi.admin.get(venueId) : skipToken,
		queryKey: [...adminVenueKey(venueId ?? ""), "venue"],
	});
}

export function useAdminVenueCourts(venueId: VenueId | null) {
	return useQuery({
		queryFn: venueId ? () => venueApi.admin.courts(venueId) : skipToken,
		queryKey: [...adminVenueKey(venueId ?? ""), "courts"],
	});
}

export function useAdminVenueHours(venueId: VenueId | null) {
	return useQuery({
		queryFn: venueId ? () => venueApi.admin.hours(venueId) : skipToken,
		queryKey: [...adminVenueKey(venueId ?? ""), "hours"],
	});
}

export function useAdminVenuePrices(venueId: VenueId | null) {
	return useQuery({
		queryFn: venueId ? () => venueApi.admin.prices(venueId) : skipToken,
		queryKey: [...adminVenueKey(venueId ?? ""), "prices"],
	});
}

/** `null` data = the venue has not created a payout account yet. */
export function useAdminVenueAccount(venueId: VenueId | null) {
	return useQuery({
		queryFn: venueId ? () => venueApi.admin.account(venueId) : skipToken,
		queryKey: [...adminVenueKey(venueId ?? ""), "account"],
	});
}

export function useAdminVenueReservations(venueId: VenueId | null, query: CalendarQuery) {
	return useQuery({
		queryFn: venueId ? () => venueApi.admin.reservations(venueId, query) : skipToken,
		queryKey: [...adminVenueKey(venueId ?? ""), "reservations", query],
	});
}

export function useAdminCancelReservation(venueId: VenueId) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			reservationId,
			reason,
		}: {
			reservationId: ReservationId;
			reason?: CancelReason;
		}) => venueApi.admin.cancelReservation(venueId, reservationId, reason),
		onSuccess: () => qc.invalidateQueries({ queryKey: adminVenueKey(venueId) }),
	});
}

/** What needs attention across every venue; refreshed by any admin venue mutation. */
export function useAdminVenueStats() {
	return useQuery({
		queryFn: () => venueApi.admin.stats(),
		queryKey: [...keys.all(), "admin", "stats"],
	});
}

export function useAdminVenueMembers(venueId: VenueId | null) {
	return useQuery({
		queryFn: venueId ? () => venueApi.admin.members.list(venueId) : skipToken,
		queryKey: [...keys.members(venueId ?? ""), "admin"],
	});
}

export function useAdminAddVenueMember(venueId: VenueId) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ email, role }: { email: string; role: StaffRole }) =>
			venueApi.admin.members.add(venueId, email, role),
		onSuccess: () => qc.invalidateQueries({ queryKey: keys.all() }),
	});
}

export function useAdminRemoveVenueMember(venueId: VenueId) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (userId: UserId) => venueApi.admin.members.remove(venueId, userId),
		onSuccess: () => qc.invalidateQueries({ queryKey: keys.all() }),
	});
}

export function useAdminSetVenueStatus(venueId: VenueId) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (status: "live" | "suspended") => venueApi.admin.setStatus(venueId, status),
		onSuccess: () => qc.invalidateQueries({ queryKey: keys.all() }),
	});
}

export function useLinkVenueLocation() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ venueId, locationId }: { venueId: VenueId; locationId: string }) =>
			venueApi.admin.linkLocation(venueId, locationId),
		onSuccess: () => qc.invalidateQueries({ queryKey: keys.all() }),
	});
}
